import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';

import { auctionsApi } from '../api/auctions-api';
import { salesApi, type SaleListParams } from '../api/sales-api';
import { salePollInterval } from '../schemas/sale-rules';

import { auctionKeys } from './auction-keys';

/** Shared by the route loader (prefetch) and the page hook (subscribe). */
export function saleListQueryOptions(storeId: string, params: SaleListParams) {
  return queryOptions({
    queryKey: auctionKeys.saleList(storeId, params),
    queryFn: () => salesApi.list(storeId, params),
    placeholderData: keepPreviousData,
  });
}

export function useSales(storeId: string, params: SaleListParams) {
  return useQuery(saleListQueryOptions(storeId, params));
}

export function saleQueryOptions(storeId: string, id: string) {
  return queryOptions({
    queryKey: auctionKeys.sale(storeId, id),
    queryFn: () => salesApi.get(storeId, id),
    refetchInterval: (query) => salePollInterval(query.state.data?.status),
  });
}

export function useSale(storeId: string, id: string) {
  return useQuery(saleQueryOptions(storeId, id));
}

/** The lots of one sale, in lot order (the backend lists a sale's auctions by lot number). */
export function saleLotsQueryOptions(storeId: string, saleId: string) {
  return queryOptions({
    queryKey: auctionKeys.saleLots(storeId, saleId),
    queryFn: () => auctionsApi.list(storeId, { saleId, limit: 100 }),
    select: (page) =>
      [...page.items].sort((a, b) => (a.sale?.lotNumber ?? 0) - (b.sale?.lotNumber ?? 0)),
  });
}

export function useSaleLots(storeId: string, saleId: string) {
  return useQuery(saleLotsQueryOptions(storeId, saleId));
}

/** Draft auctions in no sale yet: the ones that can join a draft sale. */
export function useLooseDraftAuctions(storeId: string, enabled: boolean) {
  return useQuery({
    queryKey: auctionKeys.looseDrafts(storeId),
    queryFn: () => auctionsApi.list(storeId, { status: 'draft', limit: 100 }),
    select: (page) => page.items.filter((auction) => auction.sale === null),
    enabled,
  });
}
